'use client';

import React from 'react';
import { cn } from '@/shared/lib/cn';

interface PulsingDotsProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const SIZE_CLASSES = {
  sm: 'w-2 h-2',
  md: 'w-3 h-3',
  lg: 'w-4 h-4',
} as const;

const GAP_CLASSES = {
  sm: 'gap-1.5',
  md: 'gap-2',
  lg: 'gap-2.5',
} as const;

/**
 * Three dots travelling as one soft wave: each dot runs the same
 * fade-and-swell keyframe, staggered so one is lighting up while the previous
 * is still fading. Single driver by design — the earlier version ran a CSS
 * pulse AND a JS step that hard-set opacity, and the two fought, which is what
 * made the dots flick like a traffic light.
 */
export function PulsingDots({ className, size = 'md' }: PulsingDotsProps) {
  return (
    <div className={cn('flex items-center', GAP_CLASSES[size], className)}>
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className={cn(
            SIZE_CLASSES[size],
            'rounded-full bg-current animate-dot-wave'
          )}
          style={{ animationDelay: `${index * 180}ms` }}
        />
      ))}
    </div>
  );
}
